/* eslint-disable no-case-declarations */
import React, { Fragment, useEffect } from 'react'
// @ts-ignore
import { Field, reduxForm } from 'redux-form'
import { compose } from 'redux'
import { useTranslation } from 'react-i18next'
import ReactLoading from 'react-loading'
import { Row, Col } from 'reactstrap'

import validate from 'Containers/Validation/validate'
import renderSelectField from 'Shared/components/form/Select'
import renderField from 'Containers/Validation/renderField'
import { CountriesProps } from '../SettingsProps'
import { Recipient, TransferwiseRequirement } from '../../../Types'

interface BankDetailsFormProps {
  pristine: boolean
  submitting: boolean
  handleSubmit: () => void
  item: TransferwiseRequirement
  handleSpecifyRequirements: (val: string, key: string) => void
  isLoading: boolean
  countries: CountriesProps[]
  recipient: Recipient
  currentCurrency: string
  currentTransferType: string
}

const REQUIRED_FIELD_ID = [
  'address.country',
  'address.city',
  'address.firstLine',
  'address.postCode',
]
const REQUIRED_FIELDS = {
  'address.country': {
    name: 'Country',
    group: [
      {
        key: 'address.country',
        name: 'Country',
        type: 'select',
        refreshRequirementsOnChange: true,
        required: true,
        example: 'Select country',
        valuesAllowed: [],
      },
    ],
  },
  'address.city': {
    name: 'City',
    group: [
      {
        key: 'address.city',
        name: 'City',
        type: 'text',
        refreshRequirementsOnChange: false,
        required: true,
        example: '',
        valuesAllowed: null,
      },
    ],
  },
  'address.firstLine': {
    name: 'Address',
    group: [
      {
        key: 'address.firstLine',
        name: 'Address',
        type: 'text',
        refreshRequirementsOnChange: false,
        required: true,
        example: '',
        valuesAllowed: null,
      },
    ],
  },
  'address.postCode': {
    name: 'Post Code',
    group: [
      {
        key: 'address.postCode',
        name: 'Post Code',
        type: 'text',
        refreshRequirementsOnChange: false,
        required: true,
        example: '',
        valuesAllowed: null,
      },
    ],
  },
}

const BankDetailsForm: React.FunctionComponent<BankDetailsFormProps> = ({
  submitting,
  handleSubmit,
  item: { fields },
  handleSpecifyRequirements,
  isLoading,
  countries,
  recipient,
  currentCurrency,
  currentTransferType,
  pristine,
}) => {
  const { t } = useTranslation()

  const setSelectOptions = (arr: Array<{ key: string; name: string }>) =>
    arr.map(({ key, name }) => ({ value: key, label: name }))

  const getDefaultSelectValue = (value: string, key: string): string | null => {
    if (
      recipient &&
      recipient.currency === currentCurrency &&
      recipient.type === currentTransferType
    ) {
      if (value) {
        return value
      }
      if (key.includes('address')) {
        // @ts-ignore
        return recipient.details.address[key.slice(8)]
      }
    }
    return null
  }

  const renderInput = ({
    // @ts-ignore
    type,
    // @ts-ignore
    name,
    // @ts-ignore
    key,
    // @ts-ignore
    valuesAllowed,
    // @ts-ignore
    refreshRequirementsOnChange,
    // @ts-ignore
    required,
    // @ts-ignore
    example,
  }) => {
    switch (type) {
      case 'date':
      case 'text':
        return (
          <Col key={key} lg={6} md={12} xl={6} xs={12}>
            <div className="form__form-group">
              <span className="form__form-group-label">{name}</span>
              <div className="form__form-group-field">
                <Field
                  component={renderField}
                  name={key}
                  placeholder={example}
                  required={required}
                  type="text"
                />
              </div>
            </div>
          </Col>
        )
      case 'radio':
      case 'select':
        let options = []

        if (key === 'address.country') {
          options = countries
        } else {
          options = valuesAllowed ? setSelectOptions(valuesAllowed) : []
        }

        return (
          <Col key={key} lg={6} md={12} xl={6} xs={12}>
            <div className="form__form-group">
              <span className="form__form-group-label">{name}</span>
              <div className="form__form-group-field">
                <div className="form__form-group-field">
                  <Field
                    component={renderSelectField}
                    defaultValue={
                      recipient &&
                      // @ts-ignore
                      getDefaultSelectValue(recipient.details[key], key)
                    }
                    menuPlacement="top"
                    name={key}
                    options={options}
                    placeholder={name}
                    required={required}
                    onChange={
                      refreshRequirementsOnChange
                        ? // @ts-ignore
                          value => handleSpecifyRequirements(value, key)
                        : null
                    }
                  />
                </div>
              </div>
            </div>
          </Col>
        )
      default:
        return <p className="red-text">Something went wrong.</p>
    }
  }

  const fieldToRender = REQUIRED_FIELD_ID.filter(fieldKey =>
    fields.every(({ group }) => group.every(({ key }) => key !== fieldKey))
  )

  return (
    <>
      {isLoading ? (
        <ReactLoading
          className="users-loading loading"
          height={24}
          type="spinningBubbles"
        />
      ) : (
        <form
          className="form change_password_form payout_form"
          onSubmit={handleSubmit}
        >
          <Row>
            <Col lg={6} md={12} xl={6} xs={12}>
              <div className="form__form-group">
                <span className="form__form-group-label">
                  Account holder name
                </span>
                <div className="form__form-group-field">
                  <Field
                    component={renderField}
                    name="accountHolderName"
                    required
                    type="text"
                  />
                </div>
              </div>
            </Col>
            {fields.map(({ group, name }) => (
              <Fragment key={name}>
                {group.map(field => renderInput(field))}
              </Fragment>
            ))}
            {fieldToRender.map(fieldId => {
              const { group } = REQUIRED_FIELDS[
                fieldId as keyof typeof REQUIRED_FIELDS
              ]
              return [...group].map(field => renderInput(field))
            })}
          </Row>
          <Col lg={12} md={12} xl={12} xs={12}>
            <button
              className="btn btn-success btn-save-changes account__btn--small"
              disabled={pristine || submitting}
              type="submit"
            >
              {t('settingsPage.saveChanges')}
            </button>
          </Col>
        </form>
      )}
    </>
  )
}

//  eslint-disable-next-line @typescript-eslint/no-explicit-any
export default compose<any>(
  reduxForm({
    form: 'bank_details_form',
    validate,
    enableReinitialize: true,
  })
)(BankDetailsForm)
